import styled from '@emotion/styled'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { twinsAcceptShare, usersCreateSubscription } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { Feature, OnboardingStep, SubscriptionStatus, UserObjective } from 'api/model'
import { COLORS, UI_SIZES } from 'app-constants'
import DeletionRequested from 'components/DeletionRequested'
import NavBar, { animationDuration } from 'components/NavBar'
import NavMenu, { accessiblePathsForCancelledSubscription } from 'components/NavMenu'
import MyTwinBot from 'components/Onboarding/MyTwinBot'
import RefreshAppAlert from 'components/RefreshAppAlert'
import { useFetchImpersonatedUser } from 'hooks/use-admin'
import { useFetchAppVersion } from 'hooks/use-app-version'
import { useFetchAuthenticatedUser } from 'hooks/use-authenticated-user'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import useFlag from 'hooks/use-flag'
import { useFetchInsightCategories } from 'hooks/use-insights'
import { useActiveOrganization } from 'hooks/use-organization'
import { useFetchCheckPermissions } from 'hooks/use-permission'
import { useFetchMyTwin } from 'hooks/use-twin'
import { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useMatch, useNavigate, useSearchParams } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { ftueFailStateAtom, navBarExpandedAtom, toastAtom } from 'state'
import { ReactComponent as JelledLogo } from '../NavBar/jelled-final-logo.svg'
import OrganizationInfoBar from './OrganizationInfoBar'
import Toast from './Toast'
import ReactGA4 from 'react-ga4'

const MainBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile: boolean }>`
  width: 100%;
  height: 100vh;
  padding: 0;
`

/**
 * Wraps the main dashboard UI.
 * Controls loading the authenticated user and redirecting if needed.
 */
const AuthenticatedContainer = () => {
  const setToast = useSetRecoilState(toastAtom)

  const { isLoading, user, setUser } = useFetchAuthenticatedUser()
  const { myTwin } = useFetchMyTwin(user?.id ?? '')
  const { refetchInsightCategories } = useFetchInsightCategories(myTwin?.id ?? '')
  const { impersonatedUser } = useFetchImpersonatedUser()
  const [activeOrganization, setActiveOrganization] = useActiveOrganization()

  // this performs a continuous polling for app version update every hour
  const { isAppVersionStale } = useFetchAppVersion()

  // we need to fetch the permissions for insights to determine if the user has access to the insights feature
  const { featurePermissions: insightsPermissions } = useFetchCheckPermissions(Feature.insights)
  const missingInsightsPermissions = useMemo(() => {
    return insightsPermissions?.missingPermissions ?? []
  }, [insightsPermissions])

  // fetch email responder permissions as well for usage in the <ConnectIntegrationsStage>
  const { featurePermissions: emailResponderPermissions } = useFetchCheckPermissions(Feature.email_responder)
  const missingEmailResponderPermissions = useMemo(() => {
    return emailResponderPermissions?.missingPermissions ?? []
  }, [emailResponderPermissions])

  // fetch advanced email responder permissions as well for usage in the <ConnectIntegrationsStage>
  useFetchCheckPermissions(Feature.advanced_email_responder)

  // this block handles the state around accepting a shared link to a twin
  const location = useLocation()
  const isTwinSharedLink = location.pathname.includes('twin-profile') && location.search.includes('shareToken')
  const twinId = isTwinSharedLink ? location.pathname.split('/')[2] : ''
  const [searchParams, setSearchParams] = useSearchParams()
  const shareToken = searchParams.get('shareToken') ?? ''
  // if there is a checkout session id, then we want to create the subscription
  const checkoutSessionId = searchParams.get('session_id') ?? ''

  const tosSigned = user?.tos_signed === true
  const hasCompany = !!user?.organization_id

  // if the user has a linked organization, their profiles array should be non empty
  const hasLinkedOrganization = user?.profiles?.length !== 0
  const hasUpdatedTopicsOfInterest = !!user?.onboarding_steps_completed.includes(
    OnboardingStep.topics_of_interest_update_finished
  )
  const hasUpdatedTwinProfile = !!user?.onboarding_steps_completed.includes(OnboardingStep.twin_profile_update_finished)

  const ftueFail = useRecoilValue(ftueFailStateAtom)

  let navigate = useNavigate()

  const invitedOrgId = searchParams.get('invite_org_id')

  const subscription = user?.profiles.find((p) => p.organization.id === activeOrganization)?.organization.subscription
  // Payments logic: if org subscription is eternal, we should not require the user to go through payment flow
  const isEternalSubscription = subscription?.product_name === 'eternal'
  const isEnterpriseSubscription = subscription?.product_name === 'enterprise'
  const isBetaSubscription = subscription?.product_name === 'beta'
  // otherwise, we should require the user to go through the payment flow

  const hasPaidSubscription = !!user?.subscription
  useEffect(() => {
    async function fetchData() {
      try {
        if (!checkoutSessionId) {
          return
        }

        const response = await usersCreateSubscription({
          stripe_checkout_session_id: checkoutSessionId,
        })
        // upon successful creation, we want to update the user object with the subscription property
        setUser((prevUser) => {
          if (!prevUser) {
            return prevUser
          }

          return {
            ...prevUser,
            subscription: response.data,
          }
        })
      } catch (e) {
        setToast({ title: 'Failed to create subscription', severity: 'error' })
      }
    }

    if (!hasPaidSubscription) {
      fetchData()
    }
  }, [activeOrganization, hasPaidSubscription, checkoutSessionId, setToast, setUser, navigate])

  const isMobile = useCheckMobileScreen()

  const navBarExpanded = useRecoilValue(navBarExpandedAtom)

  const hasSubscription =
    !!user?.subscription || isEnterpriseSubscription || isEternalSubscription || isBetaSubscription

  const flags = useFlag('disable-trends-build')
  const isTrendsBuildDisabled = flags.feature.active

  useEffect(() => {
    if (!hasSubscription || !hasUpdatedTwinProfile || isTrendsBuildDisabled) {
      return
    }

    // we should only refetch insight categories if the user has finished onboarding
    // every five seconds, we check to see if insights onboarding is 100% and if it is, we stop refetching
    const interval = setInterval(() => {
      if (myTwin?.insights_onboarding_percent === 100) {
        clearInterval(interval)
        return
      }
      refetchInsightCategories()
    }, 5000)
    return () => clearInterval(interval)
  }, [
    myTwin?.insights_onboarding_percent,
    refetchInsightCategories,
    hasSubscription,
    hasUpdatedTwinProfile,
    isTrendsBuildDisabled,
  ])

  useEffect(() => {
    if (process.env.REACT_APP_MOCK_API === 'true') {
      // In MOCK_API mode the randomized user may not have tos_signed or an organization_id.
      return
    }

    if (user === null) {
      return
    }

    if (ftueFail) {
      navigate('/onboarding/fail', { replace: true })
      return
    }

    if ((!tosSigned || !hasCompany || !hasLinkedOrganization || !hasCompany) && !hasSubscription) {
      // User has not accepted terms of service, forward to /onboarding so they can accept
      navigate('/onboarding', { replace: true })
      return
    }

    if (!user.role && searchParams.get('stage') === 'user_role' && !hasSubscription) {
      navigate('/onboarding?stage=user_role', { replace: true })
      return
    }

    if (!user.function && searchParams.get('stage') === 'function' && !hasSubscription) {
      navigate('/onboarding?stage=function', { replace: true })
      return
    }

    if (!user.objectives.length && searchParams.get('stage') === 'main_objective' && !hasSubscription) {
      navigate('/onboarding?stage=main_objective', { replace: true })
      return
    }

    // if the user's objective contains email_management, we want to redirect them to the topics of interest page
    if (
      !hasUpdatedTopicsOfInterest &&
      user.objectives.includes(UserObjective.email_management) &&
      searchParams.get('stage') === 'topics_of_interest' &&
      !hasSubscription
    ) {
      navigate('/onboarding?stage=topics_of_interest', { replace: true })
      return
    }

    // otherwise, we want to redirect the user to the twin profile page
    if (
      !hasUpdatedTwinProfile &&
      (missingInsightsPermissions.length > 0 || missingEmailResponderPermissions.length > 0) &&
      !hasSubscription &&
      !checkoutSessionId
    ) {
      navigate('/onboarding?stage=create_twin_profile', { replace: true })
      return
    }

    // we only want to show this stage if the user has missing insights permissions and does not yet have a subscription
    if (
      hasUpdatedTwinProfile &&
      (missingInsightsPermissions.length > 0 || missingEmailResponderPermissions.length > 0) &&
      !hasSubscription
    ) {
      navigate('/onboarding?stage=connect_integrations', { replace: true })
      return
    }

    if (
      hasUpdatedTwinProfile &&
      missingInsightsPermissions.length === 0 &&
      missingEmailResponderPermissions.length === 0 &&
      !hasSubscription
    ) {
      navigate(`/onboarding?stage=subscribe`, { replace: true })
      return
    }

    // if the user has a subscription but it is cancelled
    if (
      hasSubscription &&
      user.subscription?.status === SubscriptionStatus.canceled &&
      !accessiblePathsForCancelledSubscription.includes(location.pathname)
    ) {
      navigate('/organization', { replace: true })
      return
    }

    // if the user has a subscription but the trial has ended
    if (hasSubscription && user.subscription?.status === SubscriptionStatus.trial_ended) {
      navigate('/trial-ended', { replace: true })
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, hasSubscription])

  useEffect(() => {
    if (!invitedOrgId || !user) {
      return
    }

    if (user.profiles.some((up) => up.organization.id === invitedOrgId)) {
      setActiveOrganization(invitedOrgId)
    }

    searchParams.delete('invite_org_id')
    setSearchParams(searchParams, { replace: true })
  }, [invitedOrgId, searchParams, setSearchParams, setActiveOrganization, user])

  useEffect(() => {
    const acceptShareLink = async () => {
      if (!activeOrganization || !twinId || !shareToken) {
        return
      }

      try {
        // if our URL contains a shared link to a twin, we need to accept the shared link to that twin, establishing a connection between a user and the twin
        await twinsAcceptShare(activeOrganization, twinId, {
          shareToken,
        })
        setToast({ title: 'Successfully accepted share link for twin', severity: 'success' })
      } catch (e) {
        setToast({ title: 'Failed to accept share link for twin', severity: 'error' })
      }
    }

    // we only want to accept the share link if we are on the twin's page with a share token
    if (isTwinSharedLink) {
      acceptShareLink()
      // upon accepting the share link, we should navigate the user to the twin's conversation page without the shareToken / orgId
      navigate(`/twin-profile/${twinId}`, { replace: true })
    }
  }, [twinId, shareToken, navigate, activeOrganization, setToast, isTwinSharedLink])

  // [Analytics] set the user id for tracking when the user is authenticated
  useEffect(() => {
    if (user) {
      ReactGA4.set({ userId: user.id })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // In future improvements to use css grid layout here, this will no longer be needed.
  const isHeadcountManagement = useMatch('/plans/:pid/headcounts/:hcid')

  if (!user || isLoading) {
    return (
      <Stack width="100vw" height="100vh" justifyContent="center" alignItems="center">
        <CircularProgress size="24px" thickness={4} style={{ color: COLORS.JELLED_PRIMARY_BLUE }} />
      </Stack>
    )
  }

  const showOrganizationBar = !!impersonatedUser

  const isOrganizationBarEnabled = !!impersonatedUser

  const renderHeader = () => {
    // if we are on the onboarding pages, we want to render a different header
    if (location.pathname.includes('/onboarding')) {
      return (
        <JelledLogo
          style={{
            width: '126px',
            height: '30px',
          }}
        />
      )
    }

    // depending on which route we're on, the text that we will display is different
    let headerText = ''

    switch (location.pathname) {
      case '/':
        headerText = 'Insights'
        break
      case '/trends':
        headerText = 'Trends'
        break
      case '/emails':
        headerText = 'Emails'
        break
      case '/gmail': // TODO: this will be deprecated
        headerText = 'Gmail'
        break
      case '/jelledbots':
        headerText = 'JelledBots'
        break
      case '/my-twin':
        headerText = 'Description'
        break
      case '/my-twin/sharing':
        headerText = 'Sharing'
        break
      case '/my-twin/notes':
        headerText = 'Notes'
        break
      case '/my-twin/emails':
        headerText = 'Emails'
        break
      case '/my-twin/calendars':
        headerText = 'Calendars'
        break
      case '/my-twin/files':
        headerText = 'Files'
        break
      case '/my-twin/instructions':
        headerText = 'Instructions'
        break
      case '/organization':
        headerText = 'Account settings'
        break
      case '/organization/personal-space':
        headerText = 'Personal space settings'
        break
      case '/organization/company':
        headerText = 'Company settings'
        break
      case '/organization/emails':
        headerText = 'Email settings'
        break
      case '/organization/data-and-privacy':
        headerText = 'Data & Privacy'
        break
      case '/organization/billing':
        headerText = 'Billing'
        break
      case '/admin':
        headerText = 'Customer account access'
        break
      case '/admin/accounts':
        headerText = 'Account management'
        break
      case '/admin/metrics':
        headerText = 'Metrics'
        break
      case '/admin/ai-metrics':
        headerText = 'AI Metrics'
        break
      case '/admin/twin-metrics':
        headerText = 'Twin Metrics'
        break
      case '/admin/prompts':
        headerText = 'Prompts'
        break
    }

    let updatedHeaderText = headerText
    if (headerText === 'Gmail') {
      if (location.search.includes('currentPage')) {
        updatedHeaderText = 'Drafts'
      } else {
        updatedHeaderText = 'Insights'
      }
    } else if (headerText === 'JelledBots') {
      if (location.search.includes('isReviews')) {
        updatedHeaderText = 'Reviews'
      } else {
        updatedHeaderText = 'Conversations'
      }
    }

    return (
      <Stack>
        <Typography variant="h4.75">{updatedHeaderText}</Typography>
      </Stack>
    )
  }

  if (user.deletion_requested) {
    return <DeletionRequested />
  }

  const isOnboarding = window.location.pathname.includes('/onboarding')

  return (
    <Stack position="relative">
      {showOrganizationBar && <OrganizationInfoBar user={user} />}
      {isMobile ? (
        <Stack
          sx={{
            marginTop: showOrganizationBar ? '48px' : '0px',
          }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            p={2}
            pt={2}
            position="relative"
            borderBottom="1px solid #ddd">
            <NavMenu user={user} />
            {renderHeader()}
          </Stack>
          <MainBox
            isMobile={isMobile}
            style={{
              paddingBottom: isHeadcountManagement ? 0 : undefined,
            }}>
            <Outlet />
          </MainBox>
          <Toast />
        </Stack>
      ) : (
        <Stack direction="row" spacing={0} justifyContent="flex-start" pt={0}>
          <Box
            flex={`0 0 ${isOnboarding ? 0 : UI_SIZES.NAV.CONTAINER_WIDTH}`}
            minWidth={isOnboarding ? 0 : UI_SIZES.NAV.CONTAINER_WIDTH}
            height={312}>
            <NavBar user={user} />
          </Box>

          <MainBox
            isMobile={isMobile}
            style={{
              display: 'block',
              alignItems: 'inherit',
              paddingBottom: isHeadcountManagement ? 0 : undefined,
              paddingLeft: navBarExpanded && !isOnboarding ? '160px' : '0px',
              transition: `paddingLeft ${animationDuration} linear`,
              backgroundColor: COLORS.GRAY_8,
              maxWidth: '100%',
              overflowX: 'hidden',
            }}>
            <Outlet />
          </MainBox>

          <Toast />
        </Stack>
      )}
      {isAppVersionStale && <RefreshAppAlert />}
      <MyTwinBot isOrganizationBarEnabled={isOrganizationBarEnabled} />
    </Stack>
  )
}

export default AuthenticatedContainer
